import { filterDOMProps } from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import type { ForwardedRef, PropsWithChildren, ReactNode } from 'react';
import { createContext, forwardRef, useContext } from 'react';
import type {
  FieldErrorProps as AriaFieldErrorProps,
  GroupProps,
  InputProps as AriaInputProps,
  LabelProps as AriaLabelProps,
  ContextValue,
} from 'react-aria-components';
import {
  composeRenderProps,
  FieldError as AriaFieldError,
  FieldErrorContext as AriaFieldErrorContext,
  Group,
  Input as AriaInput,
  Label,
  Provider,
  TextContext as AriaTextContext,
} from 'react-aria-components';

import { ReportIcon } from '@infinex/ui-icons';
import { IconContext } from '@infinex/ui-icons/context';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useContextProps } from '@infinex/ui-styles';
import { UNICODE_CHARS } from '@infinex/ui-utils/constants';

import { CenterBaseline, Text, TextContext } from '../content';
import { SurfaceContext } from '../surface';

// Common props
// -----------------------------------------------------------------------------

export type CommonFieldProps = {
  /**
   * The label of the field. For "visually hidden" labels, use the `aria-label`
   * attribute.
   */
  label?: ReactNode;
  /**
   * Hint text provides extra context or instruction about what a user should
   * input in the field.
   */
  hint?: ReactNode;
  /**
   * Error messages inform the user when the input does not meet validation
   * criteria. Use with `validationBehavior` of "aria".
   */
  errorMessage?: ReactNode;
  /**
   * Avoid placeholders, which are inaccessible and disappear during input.
   * Prefer `hint` text to offer essential information.
   */
  placeholder?: string;
  /**
   * The prominence of the field. Use "low" prominence when the field is less
   * important than surrounding controls.
   * @default default
   */
  prominence?: 'low' | 'default';
  /**
   * The size of the field's input and related elements.
   * @default medium
   */
  size?: 'small' | 'medium' | 'large';
};

/**
 * Default styles for the root element of a form field. Use this function to
 * apply consistent styling to the root container of field components.
 */
export function fieldRootStyles() {
  return 'flex flex-col items-start gap-2 text-start';
}

// Field context
// -----------------------------------------------------------------------------

type FieldContextValue = {
  size: 'small' | 'medium' | 'large';
  isDisabled?: boolean;
  isInvalid?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
};

export const FieldContext = createContext<FieldContextValue>({
  isDisabled: false,
  isInvalid: false,
  isReadOnly: false,
  isRequired: false,
  size: 'medium',
});

// Field label
// -----------------------------------------------------------------------------

export type FieldLabelProps = AriaLabelProps;

/**
 * A thin wrapper around react-aria-components' `Label` component, for
 * specific styling and behaviour.
 */
export const FieldLabel = forwardRef(function FieldLabel(
  props: FieldLabelProps,
  ref: ForwardedRef<HTMLLabelElement>
) {
  const { children, className, ...otherProps } = props;
  const fieldCtx = useContext(FieldContext);

  return (
    <Label
      ref={ref}
      className={cn(
        'text-default cursor-default leading-none',
        {
          'text-xs font-medium': fieldCtx.size === 'small',
          'text-sm font-medium': fieldCtx.size === 'medium',
          'text-base font-medium': fieldCtx.size === 'large',
          'text-disabled': fieldCtx.isDisabled,
        },
        className
      )}
      {...otherProps}
    >
      {children}

      {fieldCtx.isRequired && (
        <span
          aria-hidden
          style={{
            fontSize: '1.125em',
            // prevent the increased font-size from affecting line-height
            position: 'absolute',
            // allow label selection w/o irrelevant characters
            userSelect: 'none',
          }}
        >
          {UNICODE_CHARS.wordJoiner}*
        </span>
      )}
    </Label>
  );
});

// Field error
// -----------------------------------------------------------------------------

export type FieldErrorProps = Omit<AriaFieldErrorProps, 'className' | 'style'> &
  UnsafeStyleProps;

/**
 * Displays validation errors for a form field.
 */
export const FieldError = forwardRef(function FieldError(
  props: FieldErrorProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { className, ...otherProps } = props;
  const { size } = useContext(FieldContext);
  const fieldErrorCtx = useContext(AriaFieldErrorContext);

  if (!fieldErrorCtx?.isInvalid) {
    return null;
  }

  return (
    <AriaFieldError
      ref={ref}
      className={cn(
        'text-critical flex items-baseline gap-1 text-xs',
        { 'text-sm': size === 'large' },
        className
      )}
      {...filterDOMProps(otherProps)}
    >
      {composeRenderProps(props.children, (children, { validationErrors }) => {
        return (
          <>
            <CenterBaseline>
              <ReportIcon size={size === 'large' ? 'small' : 'xsmall'} />
            </CenterBaseline>
            <span>{children || validationErrors.join(' ')}</span>
          </>
        );
      })}
    </AriaFieldError>
  );
});

// Help text
// -----------------------------------------------------------------------------

const HelpTextContext = createContext<boolean>(false);

export type HelpTextProps = {
  /**
   * The description explains requirements or adds supplementary context for how
   * to successfully interact with a component.
   */
  description?: ReactNode;
} & Pick<AriaFieldErrorProps, 'children'> &
  DOMProps &
  UnsafeStyleProps;

/**
 * Help text provides either an informative description or an error message that
 * gives more context about what a user needs to input.
 */
export const HelpText = forwardRef(function HelpText(
  props: HelpTextProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { description, ...otherProps } = props;
  const { isInvalid, size } = useContext(FieldContext);

  if (!isInvalid && description) {
    return (
      <Text
        {...otherProps}
        ref={ref}
        slot="description"
        className={cn(
          // min-height: match icon size, prevents layout shift when error message shown
          'text-secondary min-h-xsmall inline-block text-xs',
          { 'min-h-small text-sm': size === 'large' },
          props.className
        )}
      >
        {description}
      </Text>
    );
  }

  return (
    <HelpTextContext value={!!description}>
      <FieldError ref={ref} {...otherProps} />
    </HelpTextContext>
  );
});

// Input
// -----------------------------------------------------------------------------

export const InputContext = createContext<
  ContextValue<InputProps, HTMLInputElement>
>({});

export type InputProps = Omit<AriaInputProps, 'className' | 'style'> &
  UnsafeStyleProps;

/**
 * A thin wrapper around react-aria-components’ `Input` component, for
 * specific styling and behaviour.
 */
export const Input = forwardRef(function Input(
  props: InputProps,
  ref: ForwardedRef<HTMLInputElement>
) {
  [props, ref] = useContextProps(props, ref, InputContext);
  const { className, ...otherProps } = props;
  const { size } = useContext(FieldContext);

  return (
    <AriaInput
      {...otherProps}
      ref={ref}
      className={cn(
        'text-emphasis w-full scroll-my-6 bg-none text-ellipsis outline-hidden',
        'placeholder:text-tertiary disabled:text-disabled read-only:text-default',
        {
          // fix for mobile safari zoom behaviour:
          // - "touch" prefix targets `hover: none + pointer: coarse` by media query
          // - increase font to 16px (min allowed)
          'px-3 text-sm pointer-coarse:text-base':
            size === 'small' || size === 'medium',
          'px-4 text-base': size === 'large',
        },
        className
      )}
    />
  );
});

// Input group
// -----------------------------------------------------------------------------

export type InputGroupProps = {
  prominence?: 'low' | 'default';
} & Omit<GroupProps, 'className' | 'style'> &
  UnsafeStyleProps;

/**
 * A container for the input element, adornments and related controls.
 * It provides consistent layout and styling.
 */
export const InputGroup = forwardRef(function InputGroup(
  props: InputGroupProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { children, className, prominence = 'default', ...otherProps } = props;

  const fieldCtx = useContext(FieldContext);
  const layer = useContext(SurfaceContext);

  return (
    // Clear "description" and "errorMessage" slots, so adornments containing
    // `Text` components don't throw.
    <AriaTextContext.Provider value={undefined}>
      <Group
        isDisabled={fieldCtx.isDisabled}
        isInvalid={fieldCtx.isInvalid}
        ref={ref}
        // Forward focus to the input element when press on a non-interactive
        // child. Mimic browser behaviour:
        // - via mouse, focus immediately
        // - via touch, make sure it's not a scroll attempt
        onPointerDown={(e) => {
          if (
            e.pointerType === 'mouse' &&
            !(e.target as Element).closest('button,input,textarea')
          ) {
            e.preventDefault();
            e.currentTarget.querySelector('input')?.focus();
          }
        }}
        onPointerUp={(e) => {
          if (
            e.pointerType !== 'mouse' &&
            !(e.target as Element).closest('button,input,textarea')
          ) {
            e.preventDefault();
            e.currentTarget.querySelector('input')?.focus();
          }
        }}
        className={cn(
          'group/field',
          '[--field-radius:var(--radius-base)]',
          'relative z-0 flex w-full',
          'bg-fill text-secondary cursor-text rounded-(--field-radius) outline-hidden',
          'motion-safe:transition-colors',
          'hover:bg-fillHover hover:text-default',
          'pressed:bg-fillPress',
          'focus-within:outline-focusRingCenter focus-within:bg-fillHover focus-within:text-default',
          'invalid:outline-critical!',
          'disabled:bg-fillDisabled disabled:text-disabled disabled:cursor-default',
          {
            'bg-fillDisabled [--focusring-width:1px]': fieldCtx.isReadOnly,
          },
          {
            'h-8 [--field-radius:var(--radius-small)]':
              fieldCtx.size === 'small',
            'h-10': fieldCtx.size === 'medium',
            'h-12': fieldCtx.size === 'large',
          },
          {
            'bg-fillTwo hover:bg-fillTwoHover focus-within:bg-fillTwoHover disabled:bg-fillTwoDisabled':
              layer === 2,
          },
          {
            'border-secondary hover:border-emphasis bg-fillAlt hover:bg-fillAltHover pressed:bg-fillAltPress focus-within:bg-fillAltHover focus-within:outline-focusRingInside border [--focusring-width:1px]':
              prominence === 'low',
          },
          className
        )}
        {...otherProps}
      >
        {children}
      </Group>
    </AriaTextContext.Provider>
  );
});

const adornmentIconMap = {
  small: '2xsmall',
  medium: 'xsmall',
  large: 'small',
} as const;

/**
 * Render a decorative element, positioned at the start or end of an input.
 */
export const Adornment = (
  props: PropsWithChildren<{ placement: 'start' | 'end' }>
) => {
  const { children, placement } = props;

  const { size } = useContext(FieldContext);
  const isLarge = size === 'large';

  const common = [
    'text-current select-none self-center shrink-0',
    {
      '-me-1': placement === 'start',
      '-ms-1': placement === 'end',
      'ms-2': placement === 'start' && size === 'small',
      'me-2': placement === 'end' && size === 'small',
      'ms-3': placement === 'start' && size === 'medium',
      'me-3': placement === 'end' && size === 'medium',
      'ms-4': placement === 'start' && size === 'large',
      'me-4': placement === 'end' && size === 'large',
    },
  ];

  return (
    <Provider
      values={[
        [
          IconContext,
          {
            size: adornmentIconMap[size],
            className: cn(common),
          },
        ],
        [
          TextContext,
          {
            className: cn(common, {
              'text-sm': !isLarge,
              'text-base': isLarge,
            }),
          },
        ],
      ]}
    >
      {children}
    </Provider>
  );
};
